import type {
  CategoryManifest,
  SuggestionsQuery,
  SuggestionsResponse,
  ChatContext,
  ChatMessage,
  ChatSession,
  ChatSessionDetail,
  ChatSessionListResponse,
  ChatSessionSummary,
  PlanDecision,
  RollbackHttpResult,
  RollbackResponse,
  SaveSessionPayload,
  SaveSessionResponse,
} from 'src/chat/types';
import { config } from 'src/utils/config';
import { truncateAtWord } from 'src/utils/truncate';
import { getStandardHeaders, performFetchWithLogging } from '../utils/helpers';
import { FetchResult } from 'src/types/TApiQueries';

const PROXY_BASE = process.env.REACT_APP_CHAT_PROXY_URL || '';
const USE_MOCK = !PROXY_BASE;

// --- Mock infrastructure ---

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function sseEvent(event: string, data: any): string {
  return `event: ${event}\ndata: ${JSON.stringify(data)}\n\n`;
}

async function streamText(
  controller: ReadableStreamDefaultController,
  encoder: TextEncoder,
  text: string,
  delayMs = 30,
) {
  const words = text.split(' ');
  for (let i = 0; i < words.length; i++) {
    const delta = i === 0 ? words[i] : ' ' + words[i];
    controller.enqueue(encoder.encode(sseEvent('text_delta', { delta })));
    await delay(delayMs);
  }
}

const MOCK_RESPONSES: Record<string, string> = {
  default:
    "I'm your AI assistant for FileSpin. I can help you search assets, create shares, manage collections, tag content, and more. Just tell me what you'd like to do!",
  hello:
    "Hello! I'm Claude, your FileSpin AI assistant. I can help you with asset management tasks like searching, tagging, sharing, and organizing your content. What would you like to work on?",
  help: "Here's what I can help with:\n\n**Asset Management**\n- Search and find assets\n- Update metadata and tags\n- Remove backgrounds from images\n- Transcode videos\n\n**Sharing**\n- Create branded share pages\n- Check share analytics\n- Manage approvals\n\n**Collections**\n- Create and organize collections\n- Add assets to collections\n\nJust describe what you need and I'll take care of it!",
};

function detectIntent(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes('plan')) return 'plan';
  if (lower.includes('error')) return 'error';
  if (lower.includes('spam')) return 'rate_limit';
  if (lower.includes('share') && lower.includes('tag')) return 'multi_tool';
  if (lower.includes('share') || lower.includes('distribute')) return 'create_share';
  if (lower.includes('metadata')) return 'update_metadata';
  if (lower.includes('tag')) return 'tag_assets';
  if (lower.includes('collection')) return 'create_collection';
  if (lower.includes('search') || lower.includes('find')) return 'search_assets';
  if (lower.includes('remove background') || lower.includes('background')) return 'remove_background';
  if (lower.includes('hello') || lower.includes('hi ') || lower === 'hi') return 'hello';
  if (lower.includes('help') || lower.includes('what can')) return 'help';
  return 'default';
}

function createMockSSEStream(
  message: string,
  context: ChatContext,
  planDecision?: PlanDecision,
): Response {
  const encoder = new TextEncoder();
  const intent = planDecision ? 'plan_decision' : detectIntent(message);

  const stream = new ReadableStream({
    async start(controller) {
      try {
        controller.enqueue(
          encoder.encode(sseEvent('session_id', { session_id: 'mock-session-1' })),
        );
        await delay(100);

        switch (intent) {
          // Plan mode: "plan" in the message proposes a plan (auto-approved
          // in Run mode); answering the card streams the outcome.
          case 'plan': {
            const run = context.agentMode === 'run';
            await streamText(controller, encoder, "Here's what I'll change.");
            controller.enqueue(
              encoder.encode(
                sseEvent('plan_proposed', {
                  plan_id: `plan_mock${Date.now().toString(36)}`,
                  summary: 'Add the tag "outdoor" to 30 photos; existing tags are kept.',
                  steps: [{ tool: 'tag_assets_bulk', description: 'Add "outdoor" to tags' }],
                  asset_count: run ? 20 : 30,
                  matched_count: run ? undefined : 145,
                  status: run ? 'approved' : 'pending',
                  auto_approved: run || undefined,
                }),
              ),
            );
            await delay(300);
            await streamText(
              controller,
              encoder,
              run ? ' Tagged 20 assets.' : ' Approve the plan to go ahead.',
            );
            break;
          }

          case 'plan_decision': {
            const approved = planDecision!.decision === 'approve';
            controller.enqueue(
              encoder.encode(
                sseEvent('plan_status', {
                  plan_id: planDecision!.plan_id,
                  status: approved ? 'approved' : 'declined',
                  asset_limit: planDecision!.asset_limit,
                }),
              ),
            );
            await streamText(
              controller,
              encoder,
              approved ? 'Done — tagged the approved assets.' : 'OK, nothing was changed. What would you like instead?',
            );
            break;
          }

          case 'error': {
            await streamText(controller, encoder, 'Let me try to process that for you...');
            await delay(500);
            controller.enqueue(
              encoder.encode(
                sseEvent('error', {
                  code: 'tool_error',
                  message: 'Something went wrong while processing your request. Please try again.',
                }),
              ),
            );
            break;
          }

          case 'rate_limit': {
            controller.enqueue(
              encoder.encode(
                sseEvent('error', {
                  code: 'rate_limited',
                  message: 'Please wait a moment before sending another message.',
                }),
              ),
            );
            break;
          }

          case 'create_share': {
            const assetCount = context.selectedCount || 3;
            await streamText(
              controller,
              encoder,
              `I'll create a share page with ${assetCount} asset${assetCount !== 1 ? 's' : ''} for you.`,
            );
            await delay(300);

            controller.enqueue(
              encoder.encode(
                sseEvent('tool_start', {
                  tool: 'create_share',
                  input: {
                    title: 'Marketing Assets Q2',
                    asset_ids: context.selectedAssetIds.length
                      ? context.selectedAssetIds
                      : ['asset-001', 'asset-002', 'asset-003'],
                    expiry_days: 30,
                  },
                }),
              ),
            );
            await delay(1800);

            controller.enqueue(
              encoder.encode(
                sseEvent('tool_result', {
                  tool: 'create_share',
                  result: {
                    share_id: 'share-mock-001',
                    title: 'Marketing Assets Q2',
                    url: 'https://share.filespin.io/share-mock-001',
                    asset_count: assetCount,
                  },
                  ui_action: {
                    type: 'navigate',
                    route: '/shares/share-mock-001',
                    queryKeys: [['shares']],
                    toast: {
                      message: 'Share created: Marketing Assets Q2',
                      type: 'success',
                    },
                  },
                }),
              ),
            );
            await delay(200);

            await streamText(
              controller,
              encoder,
              `Done! I've created the share **"Marketing Assets Q2"** with ${assetCount} assets. The share link is ready and set to expire in 30 days.`,
            );
            break;
          }

          case 'tag_assets': {
            const count = context.selectedCount || 5;
            await streamText(
              controller,
              encoder,
              `I'll tag ${count} asset${count !== 1 ? 's' : ''} for you.`,
            );
            await delay(300);

            controller.enqueue(
              encoder.encode(
                sseEvent('tool_start', {
                  tool: 'tag_assets_bulk',
                  input: {
                    asset_ids: context.selectedAssetIds.length
                      ? context.selectedAssetIds
                      : ['asset-001', 'asset-002', 'asset-003', 'asset-004', 'asset-005'],
                    tags: ['marketing', 'q2-2026', 'product'],
                  },
                }),
              ),
            );
            await delay(1500);

            controller.enqueue(
              encoder.encode(
                sseEvent('tool_result', {
                  tool: 'tag_assets_bulk',
                  result: {
                    tagged_count: count,
                    tags: ['marketing', 'q2-2026', 'product'],
                    rollback_op_id: `mock_op_tag_${Date.now()}`,
                    can_rollback: true,
                  },
                  ui_action: {
                    type: 'toast',
                    queryKeys: [['assets']],
                    toast: {
                      message: `Tagged ${count} assets`,
                      type: 'success',
                    },
                  },
                }),
              ),
            );
            await delay(200);

            await streamText(
              controller,
              encoder,
              `Done! I've added the tags **marketing**, **q2-2026**, and **product** to ${count} assets.`,
            );
            break;
          }

          case 'update_metadata': {
            const assetId = context.currentAssetId
              || context.selectedAssetIds[0]
              || 'asset-001';
            await streamText(
              controller,
              encoder,
              "I'll update the metadata on this asset.",
            );
            await delay(300);

            controller.enqueue(
              encoder.encode(
                sseEvent('tool_start', {
                  tool: 'update_metadata',
                  input: {
                    asset_id: assetId,
                    metadata: { campaign: 'fall-2026', status: 'approved' },
                  },
                }),
              ),
            );
            await delay(1500);

            controller.enqueue(
              encoder.encode(
                sseEvent('tool_result', {
                  tool: 'update_metadata',
                  result: {
                    asset_id: assetId,
                    updated_keys: ['campaign', 'status'],
                    rollback_op_id: `mock_op_meta_${Date.now()}`,
                    can_rollback: true,
                  },
                  ui_action: {
                    type: 'toast',
                    queryKeys: [['assets'], ['asset', assetId]],
                    toast: {
                      message: 'Metadata updated',
                      type: 'success',
                    },
                  },
                }),
              ),
            );
            await delay(200);

            await streamText(
              controller,
              encoder,
              `Done! I've set **campaign** to "fall-2026" and **status** to "approved" on ${assetId}.`,
            );
            break;
          }

          case 'create_collection': {
            await streamText(
              controller,
              encoder,
              "I'll create a new collection for you.",
            );
            await delay(300);

            controller.enqueue(
              encoder.encode(
                sseEvent('tool_start', {
                  tool: 'create_collection',
                  input: { name: 'New Collection', description: 'Created via AI assistant' },
                }),
              ),
            );
            await delay(1200);

            controller.enqueue(
              encoder.encode(
                sseEvent('tool_result', {
                  tool: 'create_collection',
                  result: {
                    collection_id: 'coll-mock-001',
                    name: 'New Collection',
                  },
                  ui_action: {
                    type: 'navigate',
                    route: '/collection/coll-mock-001',
                    queryKeys: [['collections']],
                    toast: {
                      message: 'Collection created: New Collection',
                      type: 'success',
                    },
                  },
                }),
              ),
            );
            await delay(200);

            await streamText(
              controller,
              encoder,
              'Done! I\'ve created the collection **"New Collection"**. You can now add assets to it.',
            );
            break;
          }

          case 'search_assets': {
            await streamText(controller, encoder, 'Let me search for that.');
            await delay(300);

            controller.enqueue(
              encoder.encode(
                sseEvent('tool_start', {
                  tool: 'search_assets',
                  input: { query: message, limit: 10 },
                }),
              ),
            );
            await delay(1000);

            controller.enqueue(
              encoder.encode(
                sseEvent('tool_result', {
                  tool: 'search_assets',
                  result: {
                    total_count: 24,
                    assets: [
                      { id: 'asset-001', filename: 'hero-banner.jpg', content_type: 'image/jpeg' },
                      { id: 'asset-002', filename: 'product-shot.png', content_type: 'image/png' },
                      { id: 'asset-003', filename: 'demo-video.mp4', content_type: 'video/mp4' },
                    ],
                    query_criteria: {
                      keyword: message,
                      content_type: '',
                      sort_by: ['upload_time_range_DESC'],
                    },
                  },
                  ui_action: {
                    type: 'navigate',
                    route: '/assets',
                    toast: {
                      message: 'Found 24 results',
                      type: 'info',
                      link: '/assets',
                    },
                    queryKeys: [['search']],
                  },
                }),
              ),
            );
            await delay(200);

            await streamText(
              controller,
              encoder,
              'I found **24 assets** matching your search. Here are the top results:\n\n1. **hero-banner.jpg** (JPEG image)\n2. **product-shot.png** (PNG image)\n3. **demo-video.mp4** (MP4 video)\n\nWould you like me to do something with these assets?',
            );
            break;
          }

          case 'remove_background': {
            const assetId = context.currentAssetId || 'asset-001';
            await streamText(
              controller,
              encoder,
              "I'll remove the background from this image.",
            );
            await delay(300);

            controller.enqueue(
              encoder.encode(
                sseEvent('tool_start', {
                  tool: 'remove_background',
                  input: { asset_id: assetId },
                }),
              ),
            );
            await delay(2000);

            controller.enqueue(
              encoder.encode(
                sseEvent('tool_result', {
                  tool: 'remove_background',
                  result: { asset_id: 'asset-new-001', original_asset_id: assetId },
                  ui_action: {
                    type: 'toast',
                    queryKeys: [['asset', 'asset-new-001']],
                    toast: {
                      message: 'Background removed',
                      type: 'success',
                      link: '/assets/asset-new-001',
                    },
                  },
                }),
              ),
            );
            await delay(200);

            await streamText(
              controller,
              encoder,
              'Done! The background has been removed. A new asset has been created with the transparent background.',
            );
            break;
          }

          case 'multi_tool': {
            const count = context.selectedCount || 3;
            await streamText(
              controller,
              encoder,
              `I'll tag and then share ${count} assets for you.`,
            );
            await delay(300);

            // First tool: tag
            controller.enqueue(
              encoder.encode(
                sseEvent('tool_start', {
                  tool: 'tag_assets_bulk',
                  input: {
                    asset_ids: context.selectedAssetIds.length
                      ? context.selectedAssetIds
                      : ['asset-001', 'asset-002', 'asset-003'],
                    tags: ['campaign', 'approved'],
                  },
                }),
              ),
            );
            await delay(1500);

            controller.enqueue(
              encoder.encode(
                sseEvent('tool_result', {
                  tool: 'tag_assets_bulk',
                  result: {
                    tagged_count: count,
                    tags: ['campaign', 'approved'],
                    rollback_op_id: `mock_op_tag_multi_${Date.now()}`,
                    can_rollback: true,
                  },
                  ui_action: {
                    type: 'toast',
                    queryKeys: [['assets']],
                    toast: { message: `Tagged ${count} assets`, type: 'success' },
                  },
                }),
              ),
            );
            await delay(400);

            await streamText(controller, encoder, 'Assets tagged. Now creating the share...');
            await delay(300);

            // Second tool: share
            controller.enqueue(
              encoder.encode(
                sseEvent('tool_start', {
                  tool: 'create_share',
                  input: {
                    title: 'Campaign Assets',
                    asset_ids: context.selectedAssetIds.length
                      ? context.selectedAssetIds
                      : ['asset-001', 'asset-002', 'asset-003'],
                  },
                }),
              ),
            );
            await delay(1800);

            controller.enqueue(
              encoder.encode(
                sseEvent('tool_result', {
                  tool: 'create_share',
                  result: {
                    share_id: 'share-mock-002',
                    title: 'Campaign Assets',
                    url: 'https://share.filespin.io/share-mock-002',
                    asset_count: count,
                  },
                  ui_action: {
                    type: 'navigate',
                    route: '/shares/share-mock-002',
                    queryKeys: [['shares']],
                    toast: { message: 'Share created: Campaign Assets', type: 'success' },
                  },
                }),
              ),
            );
            await delay(200);

            await streamText(
              controller,
              encoder,
              `All done! I've tagged ${count} assets with **campaign** and **approved**, then created a share page **"Campaign Assets"**.`,
            );
            break;
          }

          default: {
            const response = MOCK_RESPONSES[intent] || MOCK_RESPONSES.default;
            await streamText(controller, encoder, response);
            break;
          }
        }

        await delay(100);
        controller.enqueue(
          encoder.encode(
            sseEvent('message_done', {
              usage: {
                input_tokens: 120 + message.length * 2,
                output_tokens: 60 + Math.floor(Math.random() * 200),
              },
            }),
          ),
        );
        controller.close();
      } catch {
        controller.close();
      }
    },
  });

  return new Response(stream, {
    headers: { 'Content-Type': 'text/event-stream' },
  });
}

// --- Mock session store (proxy-style, legacy) ---

let mockSessions: ChatSession[] = [
  {
    id: 'mock-session-1',
    created_at: new Date(Date.now() - 3600000).toISOString(),
    last_activity: new Date().toISOString(),
    message_count: 4,
    preview: 'Create a share with my selected images...',
  },
];

// --- Mock session store (SaaS backend) ---

interface MockBackendSession {
  session_id: string;
  title: string;
  preview: string;
  messages: ChatMessage[];
  message_count: number;
  tool_calls: number;
  total_tokens: number;
  created_at: string;
  updated_at: string;
}

let mockBackendSessions: MockBackendSession[] = [
  {
    session_id: 'mock-session-hist-1',
    title: 'Share marketing images',
    preview: 'Share my selected images with the marketing team',
    messages: [
      {
        id: 'msg-h1-1',
        role: 'user',
        content: 'Share my selected images with the marketing team',
        timestamp: Date.now() - 7200000,
      },
      {
        id: 'msg-h1-2',
        role: 'assistant',
        content: 'I\'ve created a share page **"Marketing Assets Q2"** with 3 assets. The share link is ready.',
        actions: [
          {
            id: 'act-h1-1',
            tool: 'create_share',
            input: { title: 'Marketing Assets Q2', asset_ids: ['asset-001', 'asset-002', 'asset-003'] },
            status: 'success',
            result: { share_id: 'share-001', title: 'Marketing Assets Q2' },
          },
        ],
        timestamp: Date.now() - 7195000,
        usage: { input_tokens: 1240, output_tokens: 380 },
      },
    ],
    message_count: 2,
    tool_calls: 1,
    total_tokens: 1620,
    created_at: new Date(Date.now() - 7200000).toISOString(),
    updated_at: new Date(Date.now() - 7195000).toISOString(),
  },
  {
    session_id: 'mock-session-hist-2',
    title: 'Tag product photos',
    preview: 'Tag all the product photos from last week with q2-2026',
    messages: [
      {
        id: 'msg-h2-1',
        role: 'user',
        content: 'Tag all the product photos from last week with q2-2026',
        timestamp: Date.now() - 86400000,
      },
      {
        id: 'msg-h2-2',
        role: 'assistant',
        content: 'Done! I\'ve added the tag **q2-2026** to 12 product photos.',
        actions: [
          {
            id: 'act-h2-1',
            tool: 'tag_assets_bulk',
            input: { tags: ['q2-2026'] },
            status: 'success',
            result: { tagged_count: 12 },
          },
        ],
        timestamp: Date.now() - 86395000,
        usage: { input_tokens: 980, output_tokens: 220 },
      },
    ],
    message_count: 2,
    tool_calls: 1,
    total_tokens: 1200,
    created_at: new Date(Date.now() - 86400000).toISOString(),
    updated_at: new Date(Date.now() - 86395000).toISOString(),
  },
  {
    session_id: 'mock-session-hist-3',
    title: 'Search for hero banners',
    preview: 'Find all hero banner images uploaded this month',
    messages: [
      {
        id: 'msg-h3-1',
        role: 'user',
        content: 'Find all hero banner images uploaded this month',
        timestamp: Date.now() - 172800000,
      },
      {
        id: 'msg-h3-2',
        role: 'assistant',
        content: 'I found **8 assets** matching "hero banner" from this month.',
        actions: [
          {
            id: 'act-h3-1',
            tool: 'search_assets',
            input: { query: 'hero banner', limit: 20 },
            status: 'success',
            result: { total_count: 8, query_criteria: { keyword: 'hero banner', content_type: '', sort_by: ['upload_time_range_DESC'] } },
            queryCriteria: { keyword: 'hero banner', content_type: '', sort_by: ['upload_time_range_DESC'] },
          },
        ],
        timestamp: Date.now() - 172795000,
        usage: { input_tokens: 850, output_tokens: 190 },
      },
    ],
    message_count: 2,
    tool_calls: 1,
    total_tokens: 1040,
    created_at: new Date(Date.now() - 172800000).toISOString(),
    updated_at: new Date(Date.now() - 172795000).toISOString(),
  },
];

// --- Mock category manifest ---
//
// Static fixture mirroring the canonical 6-category layout from
// internal/prompts/prompts.go (GetCategoryManifest). It only needs to
// be representative — designers iterate on the picker layout off this,
// not on every prompt that lands in production. Showcase prompts are
// listed first within each category, matching the server-side
// ordering.

function getMockCategoryManifest(): CategoryManifest {
  return {
    categories: [
      {
        id: 'campaign-media',
        title: 'Campaign & Media',
        icon: 'megaphone',
        description: 'Find, transform, package, and ship campaign assets in one prompt',
        prompts: [
          { name: 'campaign_launch_kit', description: 'End-to-end launch kit: find, transform, share, notify', required_arguments: ['campaign_name', 'tag'] },
          { name: 'create_branded_gallery', description: 'Curate a branded gallery share page' },
          { name: 'create_share_using_template', description: 'Create a share page from a saved template', required_arguments: ['template_name'] },
          { name: 'save_assets_to_collection', description: 'Add the matched assets to a collection' },
        ],
      },
      {
        id: 'product-pipelines',
        title: 'Product Pipelines',
        icon: 'package',
        description: 'Generate channel-ready product variants and push to PIM, marketplaces, or wholesale',
        prompts: [
          { name: 'daily_new_arrivals_pipeline', description: 'Process today\'s new product images for all channels' },
          { name: 'process_product_images', description: 'Generate ecommerce variants from raw product shots' },
          { name: 'marketplace_product_images', description: 'Resize and crop for Amazon / eBay / Shopify' },
        ],
      },
      {
        id: 'live-event-highlights',
        title: 'Live Event Highlights',
        icon: 'camera',
        description: 'Same-day event coverage — face-tag, generate variants, distribute to sponsors and press',
        prompts: [
          { name: 'conference_photo_distribution', description: 'Tag, package, and notify per session and per speaker' },
          { name: 'event_photo_workflow', description: 'Tag and organise event photos by session' },
          { name: 'sponsor_share_page', description: 'Build a sponsor-branded share page' },
          { name: 'find_guest_photos', description: 'Use a face reference to find guest photos' },
          { name: 'guest_photo_watermark', description: 'Watermark guest photos before distribution' },
          { name: 'create_press_kit_share_page', description: 'Bundle press-ready assets onto a share page' },
          { name: 'create_video_showcase_share_page', description: 'Highlight reel share page with video clips' },
        ],
      },
      {
        id: 'brand-review',
        title: 'Brand Approval & Review',
        icon: 'shield-check',
        description: 'Route share pages through stakeholder approval before publish',
        prompts: [
          { name: 'client_handoff_package', description: 'Build a client review share with approvals + analytics' },
          { name: 'create_media_review_page', description: 'Set up a review/approval share page' },
          { name: 'check_approval_status', description: 'Check pending approvals on a share' },
          { name: 'view_share_analytics', description: 'Show share view + download analytics' },
        ],
      },
      {
        id: 'dam-migration',
        title: 'DAM Migration & Backfill',
        icon: 'download-cloud',
        description: 'Pull from your old DAM, file share, or bucket. De-dup, auto-tag, and organise on the way in',
        prompts: [
          { name: 'legacy_dam_cutover', description: 'Migrate from a legacy DAM with de-dup, tag, organise', required_arguments: ['source_name'] },
          { name: 'tag_assets', description: 'Bulk tag assets via metadata schema' },
          { name: 'find_addon_processed_assets', description: 'Verify which uploads got processed by add-ons' },
        ],
      },
      {
        id: 'quick-actions',
        title: 'Quick Actions',
        icon: 'zap',
        description: 'Single-asset and ad-hoc operations — background removal, social variants, video clip, watermark, face search, and library hygiene',
        prompts: [
          { name: 'remove_image_background', description: 'Remove the background from this asset' },
          { name: 'create_social_media_kit_share_page', description: 'Generate social media variants' },
          { name: 'transcode_video_asset', description: 'Transcode a video asset' },
          { name: 'create_video_clip', description: 'Create a clip from a video asset', required_arguments: ['asset_id', 'start', 'duration'] },
          { name: 'add_text_watermark', description: 'Add a text watermark' },
          { name: 'find_person_photos', description: 'Find photos of a known person' },
          { name: 'audit_asset_metadata', description: 'Find assets with missing or inconsistent metadata', required_arguments: ['keyword'] },
          { name: 'search_assets_needing_review', description: 'Find older assets due for usage-rights review' },
          { name: 'standardize_metadata', description: 'Normalise inconsistent metadata values', required_arguments: ['keyword', 'field', 'new_value'] },
        ],
      },
    ],
  };
}

// --- Public API (same signatures for mock and real) ---

export async function sendChatMessage(params: {
  message: string;
  context: ChatContext;
  sessionId?: string;
  mcpPrompt?: string;
  mcpPromptArgs?: Record<string, string>;
  planDecision?: PlanDecision;
  accessToken?: string;
  signal?: AbortSignal;
}): Promise<Response> {
  if (USE_MOCK) {
    return createMockSSEStream(params.message, params.context, params.planDecision);
  }

  const body: Record<string, any> = {
    message: params.message,
    context: params.context,
    session_id: params.sessionId,
  };
  if (params.mcpPrompt) {
    body.mcp_prompt = params.mcpPrompt;
    if (params.mcpPromptArgs) {
      body.mcp_prompt_args = params.mcpPromptArgs;
    }
  }
  if (params.planDecision) {
    body.plan_decision = params.planDecision;
  }

  return fetch(`${PROXY_BASE}/api/v1/chat/messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: params.accessToken || '',
    },
    body: JSON.stringify(body),
    signal: params.signal,
  });
}

/**
 * Cancel an in-flight chat turn. Tells the proxy to stop the running
 * agentic loop (Claude + tool calls) for the given turn id. The proxy
 * persists whatever partial assistant text was already streamed and
 * emits a `message_cancelled` SSE event on the original stream.
 *
 * Fire-and-forget from the caller's perspective: the client also aborts
 * its own fetch, so a failed/duplicate cancel is harmless. Mock mode is
 * a no-op (there is no server to cancel).
 */
export async function cancelChatTurn(
  turnId: string,
  accessToken?: string,
): Promise<void> {
  if (USE_MOCK || !turnId) return;

  try {
    await fetch(`${PROXY_BASE}/api/v1/chat/cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: accessToken || '',
      },
      body: JSON.stringify({ turn_id: turnId }),
    });
  } catch {
    // The local fetch abort still stops the stream; a cancel POST that
    // fails to reach the proxy is non-fatal.
  }
}

// --- Improve prompt (POST /api/v1/chat/improve-prompt) ---
// Single non-streaming JSON in / JSON out. Asks the chat-proxy to rewrite
// the user's draft into a clearer, more specific prompt before they send
// it. Tokens are billed against the user's normal chat credits via the
// proxy's TokenTracker (Kind="improve_prompt").

export interface ImprovePromptResult {
  improved: string;
  no_change?: boolean;
}

export interface ImprovePromptError {
  error: string;
  message: string;
  status: number;
}

export type ImprovePromptResponse =
  | { ok: true; data: ImprovePromptResult }
  | { ok: false; error: ImprovePromptError };

function buildMockImprovedPrompt(draft: string, ctx: ChatContext): string {
  const trimmed = draft.trim();
  // Deterministic, context-aware mock so designers can iterate on the
  // UX without standing up the chat-proxy. "Already clear" hint test
  // cases fire when the draft starts with an upper-case letter and
  // ends with punctuation — the heuristic the real Haiku rewriter
  // mirrors closely enough for visual review.
  if (/^[A-Z].*[.!?]$/.test(trimmed) && trimmed.length > 30) {
    return trimmed;
  }
  const subject =
    ctx.selectedCount > 0
      ? `the ${ctx.selectedCount} selected ${ctx.selectedAssetTypes[0]?.startsWith('video/') ? 'videos' : 'assets'}`
      : ctx.currentAssetId
        ? 'this asset'
        : ctx.currentCollectionId
          ? 'this collection'
          : ctx.currentShareId
            ? 'this share'
            : 'these assets';
  const verb = trimmed
    .replace(/^(please|can you|could you|i want to|i need to|i'd like to)\s+/i, '')
    .replace(/^[a-z]/, (c) => c.toUpperCase());
  return verb.endsWith('.') ? verb : `${verb} for ${subject}.`;
}

export async function improvePrompt(params: {
  draft: string;
  context: ChatContext;
  mcpPrompt?: string;
  mcpPromptArgs?: Record<string, string>;
  accessToken?: string;
}): Promise<ImprovePromptResponse> {
  if (USE_MOCK) {
    await delay(400);
    const improved = buildMockImprovedPrompt(params.draft, params.context);
    return {
      ok: true,
      data: {
        improved,
        no_change: improved.trim() === params.draft.trim(),
      },
    };
  }

  const body: Record<string, any> = {
    draft: params.draft,
    context: params.context,
  };
  if (params.mcpPrompt) {
    body.mcp_prompt = params.mcpPrompt;
    if (params.mcpPromptArgs) {
      body.mcp_prompt_args = params.mcpPromptArgs;
    }
  }

  let res: Response;
  try {
    res = await fetch(`${PROXY_BASE}/api/v1/chat/improve-prompt`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: params.accessToken || '',
      },
      body: JSON.stringify(body),
    });
  } catch (err: any) {
    return {
      ok: false,
      error: {
        error: 'network_error',
        message: err?.message || 'Network error',
        status: 0,
      },
    };
  }

  let parsed: any;
  try {
    parsed = await res.json();
  } catch {
    parsed = { error: 'parse_error', message: `HTTP ${res.status}` };
  }

  if (!res.ok) {
    return {
      ok: false,
      error: {
        error: parsed?.error || 'unknown_error',
        message: parsed?.message || `HTTP ${res.status}`,
        status: res.status,
      },
    };
  }

  return {
    ok: true,
    data: {
      improved: typeof parsed?.improved === 'string' ? parsed.improved : params.draft,
      no_change: Boolean(parsed?.no_change),
    },
  };
}

// --- Rollback (POST /api/v1/chat/rollback/{op_id}) ---
// Single-shot, non-SSE. See chat-rollback-ui-handoff.md for the contract.

function buildMockRollbackResult(opId: string): RollbackHttpResult {
  if (opId === 'mock_op_expired') {
    return {
      ok: false,
      status: 400,
      body: {
        code: 'op_not_found_or_expired',
        message: 'Rollback record not found or expired (records last 24 hours).',
      },
    };
  }
  if (opId === 'mock_op_consumed') {
    return {
      ok: false,
      status: 400,
      body: { code: 'op_already_consumed', message: 'Rollback already consumed.' },
    };
  }
  if (opId === 'mock_op_forbidden') {
    return {
      ok: false,
      status: 403,
      body: { code: 'forbidden', message: "You don't have permission to undo this change." },
    };
  }
  if (opId === 'mock_op_failed') {
    return {
      ok: false,
      status: 502,
      body: { code: 'rollback_failed', message: 'Backend rollback failed; please retry.' },
    };
  }
  if (opId === 'mock_op_unavailable') {
    return {
      ok: false,
      status: 503,
      body: { code: 'rollback_unavailable', message: 'Undo is temporarily unavailable.' },
    };
  }

  const partial = opId === 'mock_op_partial';
  const unremovable = opId === 'mock_op_unremovable' ? ['new_field_we_added'] : [];
  return {
    ok: true,
    status: 200,
    body: {
      ok: true,
      result: {
        summary: partial
          ? 'Rolled back metadata change across 6/8 assets'
          : 'Rolled back metadata change across 8/8 assets',
        op_id: opId,
        tool_undone: 'tag_assets_bulk',
        schema_id: 1,
        asset_results: [
          { asset_id: 'asset-001', success: true },
          { asset_id: 'asset-002', success: true },
          ...(partial
            ? [{ asset_id: 'asset-003', success: false, error: 'mock_failure' }]
            : [{ asset_id: 'asset-003', success: true }]),
        ],
        unremovable_keys: unremovable,
      },
      ui_action: {
        type: 'toast',
        queryKeys: [['assets']],
        toast: { message: 'Metadata change reverted', type: 'success' },
      },
    },
  };
}

export async function rollbackChatOp(params: {
  opId: string;
  sessionId?: string;
  accessToken?: string;
}): Promise<RollbackHttpResult> {
  if (USE_MOCK) {
    await delay(400);
    return buildMockRollbackResult(params.opId);
  }

  const qs = params.sessionId ? `?session_id=${encodeURIComponent(params.sessionId)}` : '';
  const url = `${PROXY_BASE}/api/v1/chat/rollback/${encodeURIComponent(params.opId)}${qs}`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: params.accessToken || '',
      },
    });
  } catch (err: any) {
    return {
      ok: false,
      status: 0,
      body: { code: 'network_error', message: err?.message || 'Network error' },
    };
  }

  let body: any;
  try {
    body = await res.json();
  } catch {
    body = { code: 'parse_error', message: `HTTP ${res.status}` };
  }

  if (!res.ok) {
    return {
      ok: false,
      status: res.status,
      body: {
        // the proxy's writeJSONError names the code `error`
        code: body?.code || body?.error || 'unknown_error',
        message: body?.message || `HTTP ${res.status}`,
      },
    };
  }

  return { ok: true, status: res.status, body: body as RollbackResponse };
}

export async function listChatSessions(accessToken?: string): Promise<ChatSession[]> {
  if (USE_MOCK) {
    return [...mockSessions];
  }

  const res = await fetch(`${PROXY_BASE}/api/v1/chat/sessions`, {
    headers: { Authorization: accessToken || '' },
  });
  const data = await res.json();
  return data.sessions;
}

export async function deleteChatSession(
  sessionId: string,
  accessToken?: string,
): Promise<void> {
  if (USE_MOCK) {
    mockSessions = mockSessions.filter((s) => s.id !== sessionId);
    return;
  }

  await fetch(`${PROXY_BASE}/api/v1/chat/sessions/${sessionId}`, {
    method: 'DELETE',
    headers: { Authorization: accessToken || '' },
  });
}

/** Query string for GET /api/v1/chat/suggestions. Unknown content types are
 *  sent as `unknown` rather than dropped: dropping them could make a partly
 *  unknown selection look all-image to the server and offer image-only chips. */
export function suggestionsSearchParams(q: SuggestionsQuery): string {
  const params = new URLSearchParams({ surface: q.surface });
  if (q.page) params.set('page', q.page);
  if (q.selected) params.set('selected', String(q.selected));
  if (q.assetTypes && q.assetTypes.length > 0) {
    params.set('asset_types', q.assetTypes.map((t) => t || 'unknown').join(','));
  }
  if (q.libraryEmpty) params.set('library_empty', '1');
  return params.toString();
}

export async function fetchChatSuggestions(
  accessToken: string | undefined,
  query: SuggestionsQuery,
): Promise<SuggestionsResponse> {
  if (USE_MOCK) {
    return { slot: 'chat.no-selection', starters: [], ...getMockCategoryManifest() };
  }

  const res = await fetch(
    `${PROXY_BASE}/api/v1/chat/suggestions?${suggestionsSearchParams(query)}`,
    { headers: { Authorization: accessToken || '' } },
  );
  if (!res.ok) {
    throw new Error(`Failed to fetch chat suggestions: ${res.status}`);
  }
  const body = await res.json();
  // A proxy that predates starters returns only `categories`.
  return { slot: body.slot ?? '', starters: body.starters ?? [], categories: body.categories ?? [] };
}

// --- SaaS Backend Chat Session APIs ---
// These call config.hostUrl directly (not the Go proxy).

function deriveSessionMeta(messages: ChatMessage[]) {
  const userMessages = messages.filter((m) => m.role === 'user');
  const preview = userMessages[0]?.content.slice(0, 100) || '';
  const title = (userMessages[0] && truncateAtWord(userMessages[0].content, 60)) || 'Untitled chat';
  let toolCalls = 0;
  let totalTokens = 0;
  for (const m of messages) {
    if (m.actions) toolCalls += m.actions.length;
    if (m.usage) totalTokens += m.usage.input_tokens + m.usage.output_tokens;
  }
  return { preview, title, toolCalls, totalTokens, messageCount: messages.length };
}

export async function saveChatSession(
  accessToken: string,
  payload: SaveSessionPayload,
): Promise<FetchResult<SaveSessionResponse>> {
  if (USE_MOCK) {
    const now = new Date().toISOString();
    const meta = deriveSessionMeta(payload.messages);
    const existing = mockBackendSessions.find((s) => s.session_id === payload.session_id);
    if (existing) {
      existing.messages = payload.messages;
      existing.title = payload.title || existing.title;
      existing.preview = meta.preview;
      existing.message_count = meta.messageCount;
      existing.tool_calls = meta.toolCalls;
      existing.total_tokens = meta.totalTokens;
      existing.updated_at = now;
    } else {
      mockBackendSessions.unshift({
        session_id: payload.session_id,
        title: payload.title || meta.title,
        preview: meta.preview,
        messages: payload.messages,
        message_count: meta.messageCount,
        tool_calls: meta.toolCalls,
        total_tokens: meta.totalTokens,
        created_at: now,
        updated_at: now,
      });
    }
    return {
      session_id: payload.session_id,
      created_at: existing?.created_at || now,
      updated_at: now,
    };
  }

  const url = `${config.hostUrl}/api/v1/ai/chat/sessions`;
  return performFetchWithLogging<SaveSessionResponse>(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(payload),
  });
}

export async function getChatSessions(
  accessToken: string,
  limit = 20,
  offset = 0,
): Promise<FetchResult<ChatSessionListResponse>> {
  if (USE_MOCK) {
    const sliced = mockBackendSessions.slice(offset, offset + limit);
    const sessions: ChatSessionSummary[] = sliced.map((s) => ({
      session_id: s.session_id,
      title: s.title,
      preview: s.preview,
      message_count: s.message_count,
      tool_calls: s.tool_calls,
      total_tokens: s.total_tokens,
      created_at: s.created_at,
      last_activity: s.updated_at,
    }));
    return { sessions, total: mockBackendSessions.length };
  }

  const url = `${config.hostUrl}/api/v1/ai/chat/sessions?limit=${limit}&offset=${offset}`;
  return performFetchWithLogging<ChatSessionListResponse>(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

// Used only for SaaS-saved interactive sessions. For scheduled-run replay,
// see getScheduleRunSession in scheduleRunsApi.ts (chat-proxy Redis store).
export async function getChatSessionDetail(
  accessToken: string,
  sessionId: string,
): Promise<FetchResult<ChatSessionDetail>> {
  if (USE_MOCK) {
    const session = mockBackendSessions.find((s) => s.session_id === sessionId);
    if (!session) {
      return {
        error: 'session_not_found',
        status: 404,
        url: '',
        message: 'Session not found',
        statusText: 'Not Found',
      };
    }
    return {
      session_id: session.session_id,
      title: session.title,
      messages: session.messages,
      created_at: session.created_at,
      updated_at: session.updated_at,
    };
  }

  const url = `${config.hostUrl}/api/v1/ai/chat/sessions/${sessionId}`;
  return performFetchWithLogging<ChatSessionDetail>(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function deleteChatSessionFromBackend(
  accessToken: string,
  sessionId: string,
): Promise<FetchResult<void>> {
  if (USE_MOCK) {
    mockBackendSessions = mockBackendSessions.filter((s) => s.session_id !== sessionId);
    return undefined as any;
  }

  const url = `${config.hostUrl}/api/v1/ai/chat/sessions/${sessionId}`;
  return performFetchWithLogging<void>(url, {
    method: 'DELETE',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}
